import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Backdrop, CircularProgress, Snackbar, Alert } from "@mui/material";
import LoadingScreen from "../../components/LoadingScreen";
import { useAuth0 } from "@auth0/auth0-react";
import { shouldRedirectToLogin } from "../../types/constant";
import { setTokenProvider } from "../auth/tokenProvider";
import FileUploadDialog from "../../components/Forms/FileUploadDialog";
import ConfirmDialog from "../../components/Forms/ConfirmDialog";
import { Dialog, DialogContent } from "@mui/material";

const PreloadContext = createContext();

export const PreloadProvider = ({ children }) => {
  const { loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const [loadingCount, setLoadingCount] = useState(0);
  const loading = loadingCount > 0;
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showNotification = useCallback((message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const withLoader = useCallback(
    async (asyncFunction) => {
      try {
        setLoadingCount((c) => c + 1);
        setTokenProvider(getAccessTokenSilently);
        await asyncFunction();
      } catch (error) {
        console.error(error);
        if (shouldRedirectToLogin(error)) {
          loginWithRedirect();
        }
        showNotification("Something went wrong.", "error");
      } finally {
        setLoadingCount((c) => c - 1);
      }
    },
    [showNotification],
  );

  // Upload Dialog State
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadConfig, setUploadConfig] = useState({
    title: "Select an Image",
    handleImageUpload: null,
    imageLink: "",
    fileUploading: false,
  });

  // Open dialog globally
  const fileUploadDialog = {
    show: ({ title, handleImageUpload, imageLink, fileUploading }) => {
      setUploadConfig({
        imageLink: imageLink || "",
        title: title || "Select an Image",
        handleImageUpload,
        fileUploading: fileUploading ?? false,
      });
      setUploadOpen(true);
    },

    hide: () => {
      setUploadConfig((prev) => ({
        ...prev,
        fileUploading: false,
      }));
      setUploadOpen(false);
    },
    setLoading: (loading) => {
      setUploadConfig((prev) => ({
        ...prev,
        fileUploading: loading,
      }));
    },
  };

  // Confirm Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: "Confirm",
    message: "",
    loading: false,
    resolve: null,
  });

  const confirmDialog = {
    show: ({ title, message }) => {
      return new Promise((resolve) => {
        setConfirmConfig({
          title: title || "Confirm",
          message: message || "",
          loading: false,
          resolve,
        });
        setConfirmOpen(true);
      });
    },

    confirm: () => {
      confirmConfig.resolve?.(true);
      // DO NOT CLOSE HERE
    },

    cancel: () => {
      confirmConfig.resolve?.(false);
      setConfirmOpen(false);
    },

    close: () => {
      setConfirmOpen(false);
    },

    setLoading: (loading) => {
      setConfirmConfig((prev) => ({ ...prev, loading }));
    },
  };

  const [imageDialog, setImageDialog] = useState({
    open: false,
    src: null,
  });

  const openImageDialog = (src) => {
    setImageDialog({ open: true, src });
  };

  const closeImageDialog = () => {
    setImageDialog({ open: false, src: null });
  };

  const providerValue = useMemo(
    () => ({
      withLoader,
      loading,
      confirmDialog,
      showNotification,
      fileUploadDialog,
      openImageDialog,
    }),
    [
      withLoader,
      loading,
      confirmDialog,
      showNotification,
      fileUploadDialog,
      openImageDialog,
    ],
  );

  return (
    <PreloadContext.Provider value={providerValue}>
      {children}

      {/* Global Loader */}
      {/*
            <Backdrop
                open={loading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 999 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>*/}

      {/* Global Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={notification.severity}
          onClose={closeNotification}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Global Upload Dialog */}
      <FileUploadDialog
        open={uploadOpen}
        handleClose={fileUploadDialog.hide}
        isUploading={uploadConfig.fileUploading}
        initialImageLink={uploadConfig.imageLink}
        handleImageUpload={async (data) => {
          await uploadConfig.handleImageUpload?.(data);
        }}
        title={uploadConfig.title}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        onConfirm={confirmDialog.confirm}
        loading={confirmConfig.loading}
        onCancel={confirmDialog.cancel}
      />

      <Dialog open={imageDialog.open} onClose={closeImageDialog} maxWidth="xl">
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: "black",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {imageDialog.src && (
            <img
              src={imageDialog.src}
              alt=""
              style={{
                maxHeight: "90vh",
                maxWidth: "90vw",
                objectFit: "contain",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </PreloadContext.Provider>
  );
};

export const usePreload = () => useContext(PreloadContext);
